import { computed, ref } from 'vue';
import { useAuthStore } from '../stores/auth';
import { isViewerRole } from '../utils/roles';
import { guestDisplayName, readDemoGuestFlag } from '../utils/demoGuest';

/**
 * The guest experience as the app's own chrome reads it (card `t_803e86a4`,
 * D12): whether a guest is in front of this screen, and what to call them.
 *
 * Two facts say so, and they answer at different moments:
 *
 *  - the `demo_guest` flag the welcome page writes before it redirects (D10) —
 *    already in storage when this document boots, while `/users/me` is still a
 *    request in flight;
 *  - the hub account's ROLE (`viewer`), which is the app's own gate and the one
 *    that survives a reload, a fresh tab, or a guest who never saw the welcome
 *    page (`utils/roles.ts` → `isViewerRole`).
 *
 * The rule between them: the role decides as soon as the identity is known, the
 * flag stands in only while it is not. An owner who once walked in as a guest
 * on this browser must not wear the pill in his own house, and a guest whose
 * `/users/me` lags a second must not see the chrome appear late — the flag
 * covers the gap and the role has the last word. Neither of them authorises
 * anything: the hub's guard is the security layer (D9), this is honesty.
 */
export function useDemoGuest() {
  const auth = useAuthStore();

  // One read: the flag is written by a page that then redirects, so by the time
  // this document runs it is storage, not a promise. A browser that refuses
  // storage reads as false (`utils/demoGuest.ts`).
  const flagged = ref(readDemoGuestFlag());

  /** Is the signed-in hub account the guest role? */
  const isViewer = computed(() => isViewerRole(auth.user?.role));

  /** Is a guest in front of this screen? (see the rule in the doc comment) */
  const isGuest = computed(() => isViewer.value || (flagged.value && !auth.user));

  /** What to call them — the nickname they typed, else the generated username. */
  const guestName = computed(() => guestDisplayName(auth.user));

  return { flagged, isViewer, isGuest, guestName };
}
